import { endOfMonth, SoftLimitProps, startOfMonth, toLocalDate } from 'components/pickers/SoftLimitCalendar'
import { dateToYMDformat, YEAR } from 'utils/date'
import useTranslation from 'hooks/useTranslation'

interface OrderCalendar {
  /** najwczesniejszy mozliwy start */
  start?: string
  /** do kiedy obiekt jest dostepny (np. kolejna rezerwacja) */
  end?: string
  maxRentalStartingDate?: string
}

export interface PickerLimits extends SoftLimitProps {
  /** twarde granice (nawigacja + wylaczone dni), YYYY-MM-DD */
  minDate?: string
  maxDate?: string
}

const formatYMD = (ymd: string) => ymd.split('-').reverse().join('.')
const maxYMD = (a: string, b?: string) => (b && b.slice(0, 10) > a ? b.slice(0, 10) : a)

/**
 * Granice dat w zamowieniu (start / koniec wynajmu) dla kalendarzy z miekkimi granicami:
 * dni spoza zakresu sa klikalne i pokazuja modal z powodem zamiast byc tylko wyszarzone.
 * Twarde granice (nawigacja) koncza sie na miesiacach, w ktorych leza miekkie granice.
 */
const useOrderDateLimits = (calendar: OrderCalendar | undefined, rentalStart?: string, minEndDate?: string) => {
  const { t } = useTranslation('calendar.blockedDate')
  const fill = (key: string, date: string) => t(key).replace('{date}', formatYMD(date))

  const isYMD = (value?: string) => !!value && /^\d{4}-\d{2}-\d{2}/.test(value)
  if (!isYMD(minEndDate)) minEndDate = undefined
  if (!isYMD(rentalStart)) rentalStart = undefined

  const today = dateToYMDformat()
  const fiveYears = dateToYMDformat(new Date(Date.now() + 5 * YEAR))
  const startMin = maxYMD(today, calendar?.start)
  const startMax = calendar?.maxRentalStartingDate?.slice(0, 10) || calendar?.end?.slice(0, 10)
  const endMax = calendar?.end?.slice(0, 10)

  const toYMD = (date?: Date) => date ? dateToYMDformat(date) : undefined

  const start: PickerLimits = {
    minDate: toYMD(startOfMonth(today)),
    maxDate: toYMD(endOfMonth(startMax)) || fiveYears,
    softMinDate: startMin,
    softMaxDate: startMax,
    getBlockedModal: (reason, date, limit) => reason === 'beforeMin'
      ? {
        title: t(dateToYMDformat(date) < today ? 'dateInPastTitle' : 'startTooEarlyTitle'),
        description: fill('startTooEarlyDesc', limit),
      }
      : { title: t('startTooLateTitle'), description: fill('startTooLateDesc', limit) },
  }

  const end: PickerLimits = {
    // od miesiaca startu - zeby dni miedzy startem a minimalnym koncem byly klikalne
    minDate: toYMD(startOfMonth(rentalStart || minEndDate || today)),
    maxDate: toYMD(endOfMonth(endMax)) || fiveYears,
    softMinDate: minEndDate,
    softMaxDate: endMax,
    getBlockedModal: (reason, _date, limit) => reason === 'beforeMin'
      ? { title: t('endTooEarlyTitle'), description: fill('endTooEarlyDesc', limit) }
      : { title: t('endTooLateTitle'), description: fill('endTooLateDesc', limit) },
  }

  /** wersja z Date - dla DateRangePickerPopup */
  const asDates = (limits: PickerLimits) => ({ ...limits, minDate: toLocalDate(limits.minDate), maxDate: toLocalDate(limits.maxDate) })

  return { start, end, startDates: asDates(start), endDates: asDates(end) }
}

export default useOrderDateLimits
